import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Divider,
  IconButton,
  Collapse,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { useForm, Controller } from "react-hook-form";
import { useParams } from "react-router-dom";

// Local imports for the form

import { QK } from "../../../../../../base/qk";
import { getCarePlans } from "../../../../../../api/services/core";
// TODO: Create and import the actual API service function
import { createChokingAssessment } from "../../../../../../api/services/assessment";
import CustomSwitch from "../../../../../FormFields/CustomSwitch";
import CustomInput from "../../../../../FormFields/CustomInput/CustomInput";
import DynamicSelectInput from "../../../../../FormFields/CustomSelect/DynamicSelectInput";
import MultiSelect from "../../../../../FormFields/CustomSelect/MultiSelect";
import {
  initialChockingFormData,
  MEDICATION_SIDE_EFFECT_OPTIONS,
  SIGNIFICANT_CHANGE_OPTIONS,
} from "./chocking.config";

// A reusable component to group a question with its conditional comment field
const QuestionBlock = ({
  control,
  watch,
  switchName,
  switchLabel,
  commentName,
}) => {
  const switchValue = watch(switchName);

  return (
    <Grid item xs={12}>
      <Controller
        name={switchName}
        control={control}
        render={({ field }) => (
          <CustomSwitch
            label={switchLabel}
            name={field.name}
            checked={field.value}
            labelPlacement="start"
            onChange={(e) => field.onChange(e.target.checked)}
          />
        )}
      />
      <Collapse in={switchValue}>
        <Box sx={{ pl: 2, pt: 1 }}>
          <Controller
            name={commentName}
            control={control}
            render={({ field }) => (
              <CustomInput
                name={field.name}
                label="Comments and Action"
                value={field.value}
                onChange={field.onChange}
                multiline
                rows={3}
              />
            )}
          />
        </Box>
      </Collapse>
    </Grid>
  );
};

const CreateChokingAssessmentModal = ({ open, handleClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { id: clientID } = useParams();

  const { data: carePlans } = useQuery([QK.carePlans], () => getCarePlans());

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: { initialChockingFormData },
  });

  // Watch for changes on these fields to conditionally render components
  const hasChange = watch("q_has_change");
  const hasSideEffects = watch("q_has_side_effects");

  const createAssessmentMutation = useMutation(
    (assessmentData) => createChokingAssessment(assessmentData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QK.chokingAssessments, clientID]);
        enqueueSnackbar("Choking assessment created successfully", {
          variant: "success",
        });
        handleCloseModal();
      },
      onError: (error) => {
        enqueueSnackbar(
          error?.response?.data?.message || "Failed to create assessment",
          { variant: "error" }
        );
      },
    }
  );

  const onSubmit = (data) => {
    // Transform the multiselect data to store only the values
    const payload = {
      ...data,
      client: clientID,
      q_change_options: data.q_change_options?.map((option) => option.value),
      q_side_effect_options: data.q_side_effect_options?.map(
        (option) => option.value
      ),
    };
    createAssessmentMutation.mutate(payload);
  };

  const handleCloseModal = () => {
    reset();
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleCloseModal} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Create Choking Risk Assessment</Typography>
          <IconButton onClick={handleCloseModal}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="care_plan"
                  control={control}
                  rules={{ required: "Care plan is required" }}
                  render={({ field }) => (
                    <DynamicSelectInput
                      options={carePlans}
                      label="Which care plan is this linked to? *"
                      name="care_plan"
                      onChange={field.onChange}
                      value={field.value}
                      error={!!errors.care_plan}
                      helperText={errors.care_plan?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <QuestionBlock
                control={control}
                watch={watch}
                switchName="q_coughs"
                switchLabel="Does the individual frequently cough before, during, or after eating/drinking?"
                commentName="q_coughs_comments"
              />
              <QuestionBlock
                control={control}
                watch={watch}
                switchName="q_is_diagnosed"
                switchLabel="Has the individual already got a diagnosis of dysphagia?"
                commentName="q_is_diagnosed_comments"
              />
              <QuestionBlock
                control={control}
                watch={watch}
                switchName="q_choking_hist"
                switchLabel="Has there been a choking incident in the last 12 months?"
                commentName="q_choking_hist_comments"
              />
              <QuestionBlock
                control={control}
                watch={watch}
                switchName="q_chest_infection_hist"
                switchLabel="Has the individual had pneumonia or recurrent chest infections in the last 12 months?"
                commentName="q_chest_infection_hist_comments"
              />
              <QuestionBlock
                control={control}
                watch={watch}
                switchName="q_can_chew"
                switchLabel="Are they able to chew a normal diet?"
                commentName="q_can_chew_comments"
              />
              <QuestionBlock
                control={control}
                watch={watch}
                switchName="q_is_diet_modified"
                switchLabel="Is their food and/or drink currently modified?"
                commentName="q_is_diet_modified_comments"
              />
              <QuestionBlock
                control={control}
                watch={watch}
                switchName="q_has_pica"
                switchLabel="Does the individual have a diagnosis of Pica?"
                commentName="q_has_pica_comments"
              />
              <QuestionBlock
                control={control}
                watch={watch}
                switchName="q_has_risk_behaviours"
                switchLabel="Does the individual have behaviours that may increase choking risk?"
                commentName="q_has_risk_behaviours_comments"
              />

              {/* Question with MultiSelect */}
              <Grid item xs={12}>
                <Controller
                  name="q_has_change"
                  control={control}
                  render={({ field }) => (
                    <CustomSwitch
                      label="Has there been a significant change in the last 12 months?"
                      name={field.name}
                      labelPlacement="start"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  )}
                />
                <Collapse in={hasChange}>
                  <Box sx={{ pl: 2, pt: 1 }}>
                    <Controller
                      name="q_change_options"
                      control={control}
                      render={({ field }) => (
                        <MultiSelect
                          options={SIGNIFICANT_CHANGE_OPTIONS}
                          placeholder="Select which and comment below"
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                    <Controller
                      name="q_change_comments"
                      control={control}
                      render={({ field }) => (
                        <CustomInput
                          name={field.name}
                          label="Comments and Action"
                          value={field.value}
                          onChange={field.onChange}
                          className="mt-3"
                          multiline
                          rows={3}
                        />
                      )}
                    />
                  </Box>
                </Collapse>
              </Grid>

              {/* Question with MultiSelect */}
              <Grid item xs={12}>
                <Controller
                  name="q_has_side_effects"
                  control={control}
                  render={({ field }) => (
                    <CustomSwitch
                      label="Is the individual experiencing side effects from medication that could increase choking risk?"
                      name={field.name}
                      labelPlacement="start"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  )}
                />
                <Collapse in={hasSideEffects}>
                  <Box sx={{ pl: 2, pt: 1 }}>
                    <Controller
                      name="q_side_effect_options"
                      control={control}
                      render={({ field }) => (
                        <MultiSelect
                          options={MEDICATION_SIDE_EFFECT_OPTIONS}
                          placeholder="Select which and comment below"
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                    <Controller
                      name="q_side_effect_comments"
                      control={control}
                      render={({ field }) => (
                        <CustomInput
                          name={field.name}
                          label="Comments and Action"
                          value={field.value}
                          onChange={field.onChange}
                          className="mt-3"
                          multiline
                          rows={3}
                        />
                      )}
                    />
                  </Box>
                </Collapse>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="summary"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      name="summary"
                      label="Overall Summary and Action Plan"
                      value={field.value}
                      onChange={field.onChange}
                      multiline
                      rows={4}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseModal} variant="outlined">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={createAssessmentMutation.isLoading}
          >
            {createAssessmentMutation.isLoading
              ? "Creating..."
              : "Create Assessment"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateChokingAssessmentModal;
